import { Controller } from '@hotwired/stimulus';
/**
 * A Stimulus controller for managing HTML dialog elements as modals.
 *
 * This controller provides methods to open, close, and remove a dialog element.
 * It also supports automatically opening the dialog when it is connected when
 * the `data-modal-open` attribute is set to "auto".
 *
 * Usage:
 *
 *    <dialog data-controller="modal" data-modal-open="auto">
 *      <!-- Modal content here -->
 *    </dialog>
 *
 * The remove action closes the dialog and removes it from the DOM, which is
 * useful for modals that are dynamically created and should be cleaned up after
 * use.
 *
 * Example of removing the DOM element when the modal closes:
 *
 *    <dialog data-controller="modal" data-action="close->modal#remove">
 *      <!-- Modal content here -->
 *    </dialog>
 */
/** @type {Controller<HTMLDialogElement>} */
export default class extends Controller {
  static targets = ['template', 'container'];

  connect() {
    if (this.element.dataset.modalOpen === 'auto') {
      this.open();
    }
  }

  open() {
    this.element.showModal();
  }

  close() {
    this.element.close();
  }

  remove() {
    this.close();
    this.element.remove();
  }

  // Restores the modal's content container to its pristine state by
  // replacing its children with a fresh clone of the <template> captured on
  // render. Runs on the dialog's own `beforetoggle` event, which fires
  // before the browser's showModal() moves focus to the first focusable
  // element — so this works regardless of how the dialog was opened
  // (showModal(), commandfor/command="show-modal", etc.) and the focus
  // lands on fresh content instead of an element we're about to remove.
  // No-ops for modals that don't render a <template>.
  reset(event) {
    if (event.newState !== 'open') return;
    if (!this.hasTemplateTarget || !this.hasContainerTarget) return;

    this.containerTarget.replaceChildren(
      this.templateTarget.content.cloneNode(true),
    );
  }
}
